/**
 * ARIA Pending Debrief Page
 * Version: 1.0.0
 *
 * ARIA-9: Pending Debrief view (new status for completed-but-not-closed jobs)
 * ARIA-11: Debrief detection logic (Bill Status != "Completed" AND scheduled date in past)
 */

import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { ClipboardCheck, Calendar, User, AlertCircle, CheckCircle } from 'lucide-react';
import type { Job } from '../types';
import { IdentifierBadge } from '../components/jobs/IdentifierBadge';

const PENDING_DEBRIEF_QUERY = gql`
  query PendingDebriefJobs {
    jobsPendingDebrief {
      id
      sytelineOrderNumber
      seNumber
      customerName
      fullAddress
      city
      identifierType
      scheduling {
        scheduledDate
        appointmentDescription
      }
      technician {
        id
        fullName
      }
      sytelineBillStatus
      pendingDebrief
      needsDebrief
      jobSummary
      description
      completedAt
    }
  }
`;

// ARIA-9: Complete debrief mutation
const COMPLETE_DEBRIEF_MUTATION = gql`
  mutation CompleteDebrief($id: ID!, $notes: String) {
    completeDebrief(id: $id, notes: $notes) {
      id
      status
      pendingDebrief
      debriefCompletedAt
    }
  }
`;

export default function PendingDebrief() {
  const { data, loading, refetch } = useQuery(PENDING_DEBRIEF_QUERY);
  const [completeDebrief, { loading: completing }] = useMutation(COMPLETE_DEBRIEF_MUTATION);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [debriefNotes, setDebriefNotes] = useState('');

  const jobs = (data?.jobsPendingDebrief || []) as Job[];

  // Handle debrief completion
  const handleCompleteDebrief = async () => {
    if (!selectedJob) return;

    try {
      await completeDebrief({
        variables: {
          id: selectedJob.id,
          notes: debriefNotes || undefined,
        },
      });
      setSelectedJob(null);
      setDebriefNotes('');
      refetch();
    } catch (error) {
      console.error('Failed to complete debrief:', error);
    }
  };

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Pending Debrief</h1>
        <p className="text-gray-600">
          Jobs that need billing/status finalization
        </p>
        {/* ARIA-11: Detection logic explanation */}
        <p className="text-xs text-gray-400 mt-1">
          Shows jobs where Bill Status is not "Completed" and scheduled date has passed
        </p>
      </header>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading pending debrief jobs...</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p>No jobs pending debrief</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="card p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">
                      {job.seNumber || job.sytelineOrderNumber}
                    </span>
                    <IdentifierBadge type={job.identifierType} />
                    {/* ARIA-9: Pending debrief badge */}
                    <span className="badge bg-amber-100 text-amber-700">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Needs Debrief
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{job.customerName}</p>
                  <p className="text-xs text-gray-500">{job.fullAddress}</p>

                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Completed:{' '}
                      {job.scheduling?.scheduledDate
                        ? new Date(job.scheduling.scheduledDate).toLocaleDateString()
                        : 'Unknown'}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {job.technician?.fullName || 'Unassigned'}
                    </span>
                  </div>

                  {/* ARIA-11: Show current bill status */}
                  <div className="mt-2">
                    <span className="text-xs text-gray-400">
                      Bill Status: <strong>{job.sytelineBillStatus || 'Not Set'}</strong>
                    </span>
                  </div>

                  {(job.scheduling?.appointmentDescription || job.jobSummary) && (
                    <p className="mt-2 text-sm text-gray-500">
                      {job.scheduling?.appointmentDescription || job.jobSummary}
                    </p>
                  )}
                </div>

                <button
                  onClick={() => setSelectedJob(job)}
                  className="btn-primary flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Complete Debrief
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Debrief Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Complete Debrief
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Job: <strong>{selectedJob.seNumber || selectedJob.sytelineOrderNumber}</strong>
              <br />
              Customer: <strong>{selectedJob.customerName}</strong>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resolution Notes (optional)
              </label>
              <textarea
                value={debriefNotes}
                onChange={(e) => setDebriefNotes(e.target.value)}
                rows={4}
                className="input"
                placeholder="Enter any notes about the job completion..."
              />
            </div>

            <p className="text-xs text-gray-500 mb-4">
              This will mark the bill status as "Completed" in SyteLine.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setSelectedJob(null);
                  setDebriefNotes('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteDebrief}
                disabled={completing}
                className="btn-primary"
              >
                {completing ? 'Completing...' : 'Complete Debrief'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
